import { takeLatest, spawn, put, select, call } from 'redux-saga/effects';
import moment from 'moment';
import apiRequest, { apiSuccess } from 'utils/api';
import { SCHEDULE_MANAGEMENT } from '../reducer';
import { appActions } from 'commons/reducer';

const getFilters = (state) => state.scheduleManagement.filters;
const getPagination = (state) => state.scheduleManagement.pagination;

function* getBookings() {
  try {
    yield put({ type: SCHEDULE_MANAGEMENT.GET_BOOKINGS });
    const filters = yield select(getFilters);
    const pagination = yield select(getPagination);
    const query = new URLSearchParams();
    
    // Filtering by createdAt as a reference since scheduledAt was removed
    if (filters.fromDate) query.append('fromDate', moment(filters.fromDate).toISOString());
    if (filters.toDate) query.append('toDate', moment(filters.toDate).toISOString());
    if (filters.producer) query.append('supplierId', filters.producer);
    if (filters.carrier) query.append('carrierId', filters.carrier);
    if (filters.loadCondition) query.append('loadCondition', filters.loadCondition);
    if (filters.operationType) query.append('operationType', filters.operationType);
    if (filters.processingSite) query.append('processingSiteId', filters.processingSite);
    if (filters.state) query.append('state', filters.state);
    if (filters.id) query.append('id', filters.id);
    
    query.append('page', (pagination.page || 0) + 1);
    query.append('rowsPerPage', pagination.rowsPerPage || 10);
    
    const response = yield apiRequest(
      `api/v1/bookings?${query.toString()}`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    yield put(apiSuccess(SCHEDULE_MANAGEMENT.GET_BOOKINGS_SUCCESS, response));
  } catch (error) {
    console.error('Error fetching bookings:', error);
  }
}

function* getStateFromApi() {
  yield takeLatest(SCHEDULE_MANAGEMENT.GET_STATE_FROM_API, function* () {
    try {
      const response = yield apiRequest('api/v1/bookings/metadata', {
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      yield put(apiSuccess(SCHEDULE_MANAGEMENT.GET_STATE_FROM_API_SUCCESS, response));
      yield call(getBookings);
    } catch (error) {
      console.error('Error fetching metadata:', error);
      yield put(appActions.setError('Error al cargar datos iniciales'));
    }
  });
}

function* setFilters() {
    yield takeLatest(SCHEDULE_MANAGEMENT.SET_FILTERS, getBookings);
    yield takeLatest(SCHEDULE_MANAGEMENT.CHANGE_FILTERS, getBookings);
    yield takeLatest(SCHEDULE_MANAGEMENT.CHANGE_PAGINATION, getBookings);
}

function* createBooking({ controls }) {
  try {
    const user = yield select((state) => state.app.user);
    const loadConditionMapping = {
      'Húmedo': 'HUMEDO',
      'Seco': 'SECO',
    };

    const mergeDateTime = (date, time) => {
      if (!date || !time) return date || null;
      const dateFormats = ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601];
      const momentDate = moment(date, dateFormats);
      if (!momentDate.isValid()) return null;

      const d = momentDate.format('YYYY-MM-DD');
      const t = time.includes('T') ? moment(time).format('HH:mm') : time;
      const combined = moment(`${d} ${t}`, 'YYYY-MM-DD HH:mm');
      return combined.isValid() ? combined.toISOString() : null;
    };

    const payload = {
      supplierId: controls.supplierId || null,
      varietyId: controls.varietyId || null,
      operationType: controls.operationType || null,
      processingSiteId: controls.processingSiteId || null,
      carrierId: controls.carrierId || null,
      truckType: controls.truckType || null,
      loadCondition: loadConditionMapping[controls.loadConditions] || controls.loadConditions || null,
      binsQty: controls.binsQty === '' || isNaN(controls.binsQty) ? 0 : Number(controls.binsQty),
            binsEmptyQty: controls.emptyBins === '' || isNaN(controls.emptyBins) ? null : Number(controls.emptyBins),
      returnBins: controls.returnBins === '' || isNaN(controls.returnBins) ? null : Number(controls.returnBins),
      superSack: controls.superSack === '' || isNaN(controls.superSack) ? null : Number(controls.superSack),
      arrivalProcessingPlant: mergeDateTime(controls.date, controls.arrivalProcessingPlant),
      arrival_processing_plant: mergeDateTime(controls.date, controls.arrivalProcessingPlant),
      arrivalFieldAt: controls.operationType === 'PICKUP' ? mergeDateTime(controls.date, controls.arrivalFieldAt) : null,
      arrival_field_at: controls.operationType === 'PICKUP' ? mergeDateTime(controls.date, controls.arrivalFieldAt) : null,
      scheduledAt: controls.date ? moment(controls.date, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601]).toISOString() : '',
      needCarrier: !!controls.needCarrier,
      userInfo: {
        userId: user?.id,
        userEmail: user?.email,
      },
      createdByUserId: user?.id,
      created_by_user_id: user?.id,
    };

    const response = yield apiRequest('api/v1/bookings', {
      method: 'post',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response && response.error) {
      let errorMessage = 'Error al crear la agenda';
      try {
        const errorData = typeof response.errorMsg === 'string' ? JSON.parse(response.errorMsg) : response;
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        errorMessage = response.errorMsg || response.message || errorMessage;
      }
      yield put(appActions.setError(errorMessage));
    } else {
        yield put({ type: 'scheduleManagement/TOGGLE_FORM', modal: 'bookingFormIsOpen' });
        yield put(appActions.setSuccess('Agenda creada con éxito'));
        yield call(getBookings);
    }
  } catch (error) {
    console.error('Error creating booking:', error);
    yield put(appActions.setError('Error inesperado al crear la agenda'));
  }
}

function* deleteBooking({ id }) {
  try {
    yield put(appActions.setLoader('delete-booking', true));
    yield apiRequest(`api/v1/bookings/${id}`, {
      method: 'delete',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    yield put(appActions.setSuccess('Agenda eliminada con éxito'));
    yield put({ type: SCHEDULE_MANAGEMENT.TOGGLE_DELETE });
    yield call(getBookings);
    yield put(appActions.setLoader('delete-booking', false));
  } catch (error) {
    console.error('Error deleting booking:', error);
    yield put(appActions.setLoader('delete-booking', false));
  }
}

function* watchCreateBooking() {
  yield takeLatest(SCHEDULE_MANAGEMENT.CREATE_RECEIPT, createBooking);
}

function* watchDeleteBooking() {
  yield takeLatest(SCHEDULE_MANAGEMENT.DELETE_RECEIPT, deleteBooking);
}

function* exportBookings() {
  yield takeLatest(SCHEDULE_MANAGEMENT.EXPORT_BOOKINGS, function* () {
    try {
      yield put(appActions.setLoader('export-bookings', true));
      const filters = yield select(getFilters);
      const query = new URLSearchParams();
      
      if (filters.fromDate) query.append('fromDate', moment(filters.fromDate).toISOString());
      if (filters.toDate) query.append('toDate', moment(filters.toDate).toISOString());
      if (filters.producer) query.append('supplierId', filters.producer);
      if (filters.carrier) query.append('carrierId', filters.carrier);
      if (filters.loadCondition) query.append('loadCondition', filters.loadCondition);
      if (filters.operationType) query.append('operationType', filters.operationType);
      if (filters.processingSite) query.append('processingSiteId', filters.processingSite);
      if (filters.state) query.append('state', filters.state);
      if (filters.id) query.append('id', filters.id);

      const response = yield apiRequest(
        `api/v1/bookings/export?${query.toString()}`,
        { 
            method: 'get',
            headers: {
                'Content-Type': 'application/json',
            } 
        },
        false,
        'agenda.xlsx'
      );
      yield put(apiSuccess(SCHEDULE_MANAGEMENT.EXPORT_BOOKINGS_SUCCESS, response));
      yield put(appActions.setLoader('export-bookings', false));
    } catch (error) {
      console.error('Error exporting bookings:', error);
      yield put(appActions.setError('Error al exportar agenda'));
      yield put(appActions.setLoader('export-bookings', false));
    }
  });
}

function* updateBooking({ id, controls }) {
  try {
    const user = yield select((state) => state.app.user);
    yield put(appActions.setLoader('update-booking', true));
    const loadConditionMapping = {
      'Húmedo': 'HUMEDO',
      'Seco': 'SECO',
    };

    const mergeDateTime = (date, time) => {
      if (!date || !time) return date || null;
      const dateFormats = ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601];
      const momentDate = moment(date, dateFormats);
      if (!momentDate.isValid()) return null;

      const d = momentDate.format('YYYY-MM-DD');
      const t = time.includes('T') ? moment(time).format('HH:mm') : time;
      const combined = moment(`${d} ${t}`, 'YYYY-MM-DD HH:mm');
      return combined.isValid() ? combined.toISOString() : null;
    };

    const payload = {
      supplierId: controls.supplierId || null,
      varietyId: controls.varietyId || null,
      operationType: controls.operationType || null,
      processingSiteId: controls.processingSiteId || null,
      carrierId: controls.carrierId || null,
      truckType: controls.truckType || null,
      loadCondition: loadConditionMapping[controls.loadConditions] || controls.loadConditions || null,
      binsQty: controls.binsQty === '' || isNaN(controls.binsQty) ? 0 : Number(controls.binsQty),
            binsEmptyQty: controls.emptyBins === '' || isNaN(controls.emptyBins) ? null : Number(controls.emptyBins),
      returnBins: controls.returnBins === '' || isNaN(controls.returnBins) ? null : Number(controls.returnBins),
      superSack: controls.superSack === '' || isNaN(controls.superSack) ? null : Number(controls.superSack),
      arrivalProcessingPlant: mergeDateTime(controls.date, controls.arrivalProcessingPlant),
      arrival_processing_plant: mergeDateTime(controls.date, controls.arrivalProcessingPlant),
      arrivalFieldAt: controls.operationType === 'PICKUP' ? mergeDateTime(controls.date, controls.arrivalFieldAt) : null,
      arrival_field_at: controls.operationType === 'PICKUP' ? mergeDateTime(controls.date, controls.arrivalFieldAt) : null,
      scheduledAt: controls.date ? moment(controls.date, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601]).toISOString() : '',
      needCarrier: !!controls.needCarrier,
      userInfo: {
        userId: user?.id,
        userEmail: user?.email,
      },
      updatedByUserId: user?.id,
      updated_by_user_id: user?.id,
    };

    const response = yield apiRequest(`api/v1/bookings/${id}`, {
        method: 'put',
        body: JSON.stringify(payload),
        headers: {
            'Content-Type': 'application/json',
        },
    });
    if (response && response.error) {
       yield put(appActions.setError(response.message || response.errorMsg || 'Error al actualizar la agenda'));
    } else {
         if (!controls.isInline) {
            yield put({ type: 'scheduleManagement/TOGGLE_FORM', modal: 'bookingFormIsOpen' });
         }
         yield put(appActions.setSuccess('Agenda actualizada con éxito'));
         yield call(getBookings);
    }
    yield put(appActions.setLoader('update-booking', false));
  } catch (error) {
    console.error('Error updating booking:', error);
    yield put(appActions.setError('Error al actualizar la agenda'));
    yield put(appActions.setLoader('update-booking', false));
  }
}

function* completeBooking({ id, arrivalAt }) {
  try {
    const user = yield select((state) => state.app.user);
    
    if (!user || !user.id) {
        console.error('Complete Booking - User missing or invalid');
        yield put(appActions.setError('No se puede completar: Usuario no identificado. Recargue la página.'));
        return;
    }

    const payload = {
      state: 'COMPLETED',
      arrival_at: arrivalAt,
      received_by_user_id: user.id,
    };

    const response = yield apiRequest(`api/v1/bookings/${id}`, {
      method: 'put',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response && response.error) {
       yield put(appActions.setError(response.message || 'Error al completar la agenda'));
    } else {
        yield put({ type: SCHEDULE_MANAGEMENT.TOGGLE_COMPLETE_MODAL });
        const state = yield select((state) => state.scheduleManagement);
        if (state.bookingFormIsOpen) {
            yield put({ type: 'scheduleManagement/TOGGLE_FORM', modal: 'bookingFormIsOpen' });
        }
        yield put(appActions.setSuccess('Agenda completada con éxito'));
        yield call(getBookings);
    }
  } catch (error) {
    console.error('Error completing booking:', error);
    yield put(appActions.setError('Error inesperado al completar la agenda'));
  }
}

function* watchCompleteBooking() {
  yield takeLatest(SCHEDULE_MANAGEMENT.COMPLETE_BOOKING, completeBooking);
}

function* watchUpdateBooking() {
  yield takeLatest(SCHEDULE_MANAGEMENT.UPDATE_BOOKING, updateBooking);
}

export default function* saga() {
  yield spawn(getStateFromApi);
  yield spawn(setFilters);
  yield spawn(watchCreateBooking);
  yield spawn(watchDeleteBooking);
  yield spawn(watchUpdateBooking);
  yield spawn(watchCompleteBooking);
  yield spawn(exportBookings);
}
